export const RISK_NEWS_SOURCE_VERSION = '1.0';

export const PREFERRED_RISK_NEWS_OUTLETS = [
    { name: 'Reuters', domains: ['reuters.com'] },
    { name: 'Associated Press', domains: ['apnews.com'] },
    { name: 'AFP', domains: ['afp.com'] },
    { name: 'BBC', domains: ['bbc.com', 'bbc.co.uk'] },
    { name: 'CNN', domains: ['cnn.com'] },
    { name: 'Bloomberg', domains: ['bloomberg.com'] },
    { name: 'The Guardian', domains: ['theguardian.com'] },
    { name: 'NPR', domains: ['npr.org'] },
    { name: 'The New York Times', domains: ['nytimes.com'] },
    { name: 'The Washington Post', domains: ['washingtonpost.com'] },
    { name: 'The Wall Street Journal', domains: ['wsj.com'] },
    { name: 'Financial Times', domains: ['ft.com'] },
    { name: 'Sky News', domains: ['news.sky.com'] },
    { name: 'Al Jazeera', domains: ['aljazeera.com', 'aljazeera.net'] },
    { name: 'France 24', domains: ['france24.com'] },
    { name: 'Deutsche Welle', domains: ['dw.com'] },
    { name: 'Euronews', domains: ['euronews.com'] },
    { name: 'ABC News Australia', domains: ['abc.net.au'] },
    { name: 'ABC News US', domains: ['abcnews.go.com'] },
    { name: 'CBS News', domains: ['cbsnews.com'] },
    { name: 'NBC News', domains: ['nbcnews.com'] },
    { name: 'CBC', domains: ['cbc.ca'] },
    { name: 'CTV News', domains: ['ctvnews.ca'] },
    { name: 'NHK', domains: ['nhk.or.jp'] },
    { name: 'Kyodo News', domains: ['kyodonews.net'] },
    { name: 'Nikkei', domains: ['nikkei.com'] },
    { name: 'CNA', domains: ['channelnewsasia.com'] },
    { name: 'The Straits Times', domains: ['straitstimes.com'] },
    { name: 'The Hindu', domains: ['thehindu.com'] },
    { name: 'South China Morning Post', domains: ['scmp.com'] }
] as const;

export const PREFERRED_RISK_NEWS_DOMAINS = PREFERRED_RISK_NEWS_OUTLETS.flatMap(outlet => [...outlet.domains]);

export function preferredRiskNewsOutlet(value: string) {
    try {
        const url = new URL(value);
        if (!/^https?:$/.test(url.protocol) || url.username || url.password)
            return null;
        const host = url.hostname.toLowerCase();
        for (const outlet of PREFERRED_RISK_NEWS_OUTLETS)
            for (const domain of outlet.domains)
                if (host === domain || host.endsWith(`.${domain}`))
                    return outlet;
    } catch {}
    return null;
}

export function preferredRiskNewsUrls(values: string[]): string[] {
    const urls: string[] = [];
    const publishers = new Set<string>();
    for (const url of Array.isArray(values) ? values : []) {
        const outlet = preferredRiskNewsOutlet(url);
        if (!outlet || publishers.has(outlet.name))
            continue;
        publishers.add(outlet.name);
        urls.push(url);
    }
    return urls;
}

export function prioritizeRiskNewsSources<T extends { url: string; publisher: string }>(sources: T[]): T[] {
    const preferred: T[] = [];
    const remaining: T[] = [];
    for (const source of sources) {
        const outlet = preferredRiskNewsOutlet(source.url);
        if (outlet) {
            preferred.push({ ...source, publisher: outlet.domains[0] });
            continue;
        }
        let publisher = source.publisher;
        try {
            const host = new URL(source.url).hostname;
            if (host === 'google.com' || host.endsWith('.google.com'))
                publisher = 'unresolved-search-publisher';
        } catch {}
        remaining.push(publisher === source.publisher ? source : { ...source, publisher });
    }
    return [...preferred, ...remaining];
}

export function riskNewsSourceInstructions(candidateUrls: string[] = []): string {
    return `SOURCE COVERAGE AND PRIORITY:
    Search for coverage of this same incident across the configured major international and regional news outlets:
    ${PREFERRED_RISK_NEWS_OUTLETS.map(outlet => `${outlet.name} (${outlet.domains.join(', ')})`).join('; ')}.
    Include available, relevant major-outlet reports in the grounded evidence and citations. Search beyond the initial source.
    Prefer original reporting and independent corroboration; syndicated copies of one report are not independent evidence.
    Retain credible local reporting and official primary sources, especially when major-outlet coverage is unavailable.
    Cite only reports actually found by Google Search that support this incident, its location, and its reported date.
    Never invent coverage, infer reliability from brand recognition alone, or raise confidence just to prefer a major outlet.
    Candidate major-outlet URLs from ingestion (discovery leads, not verified evidence): ${JSON.stringify(preferredRiskNewsUrls(candidateUrls))}`;
}
